import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import useFilterOrderStore from '@/stores/useFilterOrderStore';
import { OrderStatusTab } from '@/types/columns';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { ORDER_TYPES, TAB_STATUS_OPTIONS } from '@/pages/orders/toolbar/toolbarConfig';

export type FilterKey = 'status' | 'type' | 'date' | 'printed' | 'partial';

export interface AppliedFilterChip {
  key: FilterKey;
  label: string;
  /** i18n key of the operator word ("is", "is any of"); empty for flag filters. */
  op: string;
  value: string;
  /** Dates/numbers: keep left-to-right inside Arabic text. */
  ltr?: boolean;
  onRemove: () => void;
}

export const ACTION_REQUIRED = 'action-required';

/** "Was partially delivered" means something only where returned parcels are listed (owner 11-02). */
export const PARTIAL_FILTER_TABS: OrderStatusTab[] = [OrderStatusTab.RTO, OrderStatusTab.INVENTORY];

const DAY = 'YYYY-MM-DD';
/** Date presets → [from, to] as YYYY-MM-DD (fed to the same applyDate as a custom range). */
export const DATE_PRESETS: { key: string; range: () => [string, string] }[] = [
  { key: 'today', range: () => [dayjs().format(DAY), dayjs().format(DAY)] },
  {
    key: 'yesterday',
    range: () => {
      const d = dayjs().subtract(1, 'day').format(DAY);
      return [d, d];
    },
  },
  {
    key: 'last_7_days',
    range: () => [dayjs().subtract(6, 'day').format(DAY), dayjs().format(DAY)],
  },
  { key: 'this_month', range: () => [dayjs().startOf('month').format(DAY), dayjs().format(DAY)] },
];

/**
 * The URL sync stores the date as {from, to}; the date panel stores [from, to].
 * Read both shapes.
 */
export const readDateRange = (value: unknown): [Dayjs | null, Dayjs | null] => {
  if (!value) return [null, null];
  const [from, to] = Array.isArray(value)
    ? value
    : [(value as { from?: unknown }).from, (value as { to?: unknown }).to];
  const asDay = (v: unknown) => (v ? dayjs(v as string) : null);
  return [asDay(from), asDay(to)];
};

/**
 * Orders toolbar filter state + actions. Same store writes the old filter row
 * made (page reset to 1 on every change), only gathered so the buttons, the
 * mobile drawer and the applied-filter chips share one source.
 */
export function useOrdersToolbarFilters(
  activeTab: OrderStatusTab,
  activeSubTab: string,
  onSubTabChange: (subTab: string) => void,
  /** Badge counts for the active bucket (GET /orders/count), keyed by sub-status. */
  statusCounts: Record<string, number> = {},
) {
  const { t } = useTranslation();
  const {
    createdAtFilter,
    orderTypeFilter,
    printedFilter,
    partiallyDeliveredFilter,
    setCurrentPage,
    setCreatedAt,
    setOrderType,
    setPrinted,
    setPartiallyDelivered,
    resetFilers,
  } = useFilterOrderStore();

  const baseOptions = TAB_STATUS_OPTIONS[activeTab] ?? [];
  // Action required has its own button outside the Filter menu (owner 11-02), so it is not a status option here.
  const statusOptions = baseOptions;
  // Only where the sub-status key IS a count key — never guessed.
  const statusCount = (key: string): number | undefined => statusCounts[key.replace(/-/g, '_')];
  const typeValues = orderTypeFilter ?? [];
  const hasType = typeValues.length > 0;
  const hasDate = !!createdAtFilter;
  const hasStatus = activeSubTab !== 'all' && activeSubTab !== ACTION_REQUIRED;
  const hasPrinted = printedFilter !== undefined;
  const partialApplies = PARTIAL_FILTER_TABS.includes(activeTab);
  const hasPartial = partialApplies && !!partiallyDeliveredFilter;
  // A filter you cannot see must not narrow the list (e.g. a shared link opened on another tab).
  useEffect(() => {
    if (!partialApplies && partiallyDeliveredFilter) setPartiallyDelivered(undefined);
  }, [partialApplies, partiallyDeliveredFilter, setPartiallyDelivered]);
  const activeCount = [hasType, hasDate, hasStatus, hasPrinted, hasPartial].filter(Boolean).length;

  const toggleType = (val: OrderTypeEnum) => {
    setOrderType(
      typeValues.includes(val) ? typeValues.filter((v) => v !== val) : [...typeValues, val],
    );
    setCurrentPage(1);
  };
  const selectStatus = (key: string) => onSubTabChange(activeSubTab === key ? 'all' : key);
  const choosePrinted = (value: boolean) => {
    setPrinted(printedFilter === value ? undefined : value);
    setCurrentPage(1);
  };
  const applyDate = (from: string, to: string) => {
    if (!from && !to) return;
    setCreatedAt([from ? dayjs(from) : dayjs().subtract(1, 'month'), to ? dayjs(to) : dayjs()]);
    setCurrentPage(1);
  };
  const clearDate = () => {
    setCreatedAt(undefined);
    setCurrentPage(1);
  };
  const clearType = () => {
    setOrderType([]);
    setCurrentPage(1);
  };
  const clearPrinted = () => {
    setPrinted(undefined);
    setCurrentPage(1);
  };
  const togglePartial = () => {
    setPartiallyDelivered(partiallyDeliveredFilter ? undefined : true);
    setCurrentPage(1);
  };
  const clearPartial = () => {
    setPartiallyDelivered(undefined);
    setCurrentPage(1);
  };
  const clearAll = () => {
    resetFilers();
    setCurrentPage(1);
    onSubTabChange('all');
  };

  const statusLabel = () => {
    if (activeSubTab === ACTION_REQUIRED) return String(t('action_required'));
    const option = statusOptions.find((o) => o.key === activeSubTab);
    return String(t(option?.labelKey ?? activeSubTab));
  };
  const [dateFrom, dateTo] = readDateRange(createdAtFilter);
  const range = [dateFrom, dateTo].map((d) => (d ? d.format(DAY) : ''));
  const datePreset = DATE_PRESETS.find((p) => p.range().join() === range.join())?.key;
  const dateLabel = datePreset ? String(t(datePreset)) : range.map((d) => d || '…').join(' – ');
  const typeLabel = ORDER_TYPES.filter((o) => typeValues.includes(o.value))
    .map((o) => String(t(o.labelKey)))
    .join(', ');

  const chips: AppliedFilterChip[] = [
    hasStatus && {
      key: 'status',
      label: String(t('status')),
      op: 'filter_is',
      value: statusLabel(),
      onRemove: () => onSubTabChange('all'),
    },
    hasType && {
      key: 'type',
      label: String(t('type')),
      op: typeValues.length > 1 ? 'filter_is_any_of' : 'filter_is',
      value: typeLabel,
      onRemove: clearType,
    },
    hasDate && {
      key: 'date',
      label: String(t('date')),
      op: 'filter_is',
      value: dateLabel,
      ltr: !datePreset,
      onRemove: clearDate,
    },
    hasPrinted && {
      key: 'printed',
      label: String(t('label_status')),
      op: 'filter_is',
      value: String(t(printedFilter ? 'printed' : 'not_printed')),
      onRemove: clearPrinted,
    },
    hasPartial && {
      key: 'partial',
      label: '',
      op: '',
      value: String(t('partial_marker_was')),
      onRemove: clearPartial,
    },
  ].filter(Boolean) as AppliedFilterChip[];

  return {
    activeSubTab,
    statusOptions,
    statusCount,
    typeValues,
    printedFilter,
    dateFrom,
    dateTo,
    datePreset,
    statusValue: hasStatus ? statusLabel() : '',
    typeLabel,
    dateLabel,
    active: {
      type: hasType,
      date: hasDate,
      status: hasStatus,
      printed: hasPrinted,
      partial: hasPartial,
    },
    activeCount,
    partialApplies,
    chips,
    toggleType,
    selectStatus,
    choosePrinted,
    applyDate,
    clearDate,
    togglePartial,
    clearAll,
  };
}

export type OrdersToolbarFilters = ReturnType<typeof useOrdersToolbarFilters>;
